import { IPD_ORDER_KINDS, IPD_ORDER_STATE, type IpdTaskView } from '@his/shared';

export const HOUR_MS = 3_600_000;
export const CHART_HOURS = 24;
export const CHART_LEAD_HOURS = 6;
const MAX_CLOCK_OFFSET_MS = 12 * HOUR_MS;

export type ChartTask = Pick<IpdTaskView, 'id' | 'orderId' | 'scheduledAt' | 'order'>;

export interface ChartColumn {
  index: number;
  start: number;
  hour: number;
  date: string;
  startsDay: boolean;
}

export interface ChartRow<T extends ChartTask> {
  orderId: string;
  order: T['order'];
  cells: T[][];
  count: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let cached = formatters.get(timeZone);
  if (!cached) {
    cached = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, cached);
  }
  return cached;
}

export function zonedClock(ms: number, timeZone: string): { date: string; hour: number; minute: number; second: number } {
  const parts = formatter(timeZone).formatToParts(new Date(ms));
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '0';
  return {
    date: `${value('year').padStart(4, '0')}-${value('month').padStart(2, '0')}-${value('day').padStart(2, '0')}`,
    hour: Number(value('hour')) % 24,
    minute: Number(value('minute')),
    second: Number(value('second')),
  };
}

export function hourStartIn(ms: number, timeZone: string): number {
  const clock = zonedClock(ms, timeZone);
  const wholeSecond = ms - (((ms % 1000) + 1000) % 1000);
  return wholeSecond - (clock.minute * 60 + clock.second) * 1000;
}

export function chartStart(nowMs: number, timeZone: string, leadHours: number = CHART_LEAD_HOURS): number {
  return hourStartIn(nowMs, timeZone) - leadHours * HOUR_MS;
}

export function shiftChart(start: number, days: number): number {
  return start + days * CHART_HOURS * HOUR_MS;
}

export function chartColumns(start: number, timeZone: string, hours: number = CHART_HOURS): ChartColumn[] {
  const columns: ChartColumn[] = [];
  for (let index = 0; index < hours; index += 1) {
    const at = start + index * HOUR_MS;
    const clock = zonedClock(at, timeZone);
    const previous = columns[index - 1];
    columns.push({ index, start: at, hour: clock.hour, date: clock.date, startsDay: !previous || previous.date !== clock.date });
  }
  return columns;
}

export function columnOf(start: number, at: number, hours: number = CHART_HOURS): number | null {
  if (!Number.isFinite(at) || at < start) return null;
  const column = Math.floor((at - start) / HOUR_MS);
  return column < hours ? column : null;
}

const STATE_RANK = new Map<string, number>(IPD_ORDER_STATE.map((state, index) => [state, index]));
const KIND_RANK = new Map<string, number>(IPD_ORDER_KINDS.map((kind, index) => [kind, index]));

function rank(map: Map<string, number>, key: string): number {
  return map.get(key) ?? map.size;
}

export function compareChartRows(a: Pick<ChartTask, 'order'>, b: Pick<ChartTask, 'order'>): number {
  return (
    rank(STATE_RANK, a.order.state) - rank(STATE_RANK, b.order.state) ||
    rank(KIND_RANK, a.order.kind) - rank(KIND_RANK, b.order.kind) ||
    a.order.label.localeCompare(b.order.label) ||
    a.order.id.localeCompare(b.order.id)
  );
}

export function chartRows<T extends ChartTask>(tasks: readonly T[], start: number, hours: number = CHART_HOURS): ChartRow<T>[] {
  const rows = new Map<string, ChartRow<T>>();
  for (const task of tasks) {
    const column = columnOf(start, Date.parse(task.scheduledAt), hours);
    if (column === null) continue;
    let row = rows.get(task.orderId);
    if (!row) {
      row = { orderId: task.orderId, order: task.order, cells: Array.from({ length: hours }, () => [] as T[]), count: 0 };
      rows.set(task.orderId, row);
    }
    row.cells[column]?.push(task);
    row.count += 1;
  }
  for (const row of rows.values()) {
    for (const cell of row.cells) cell.sort((a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt) || a.id.localeCompare(b.id));
  }
  return [...rows.values()].sort(compareChartRows);
}

export function nowMarker(start: number, nowMs: number, hours: number = CHART_HOURS): { column: number; fraction: number } | null {
  if (nowMs < start || nowMs >= start + hours * HOUR_MS) return null;
  const offset = (nowMs - start) / HOUR_MS;
  const column = Math.floor(offset);
  return { column, fraction: offset - column };
}

export function clockOffset(serverTime: string | null | undefined, receivedAt: number): number {
  const server = serverTime ? Date.parse(serverTime) : Number.NaN;
  if (!Number.isFinite(server) || !receivedAt) return 0;
  const offset = server - receivedAt;
  return Math.abs(offset) > MAX_CLOCK_OFFSET_MS ? 0 : offset;
}
